import { useState, useRef, useEffect, useCallback } from 'react';
import { useSample } from '../SampleContext';
import { useLayout } from '../LayoutContext';

interface WebcamCaptureProps {
    setScreen: (screen: 'intro' | 'recorder' | 'uploader' | 'webcam' | 'form') => void;
}

const isValidHexColor = (c: string): boolean => /^#([0-9A-F]{3}){1,2}$/i.test(c);

const WebcamCapture: React.FC<WebcamCaptureProps> = ({ setScreen }) => {
    const { setCapturedImage } = useSample();
    const { layout } = useLayout();
    const videoRef = useRef<HTMLVideoElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const streamRef = useRef<MediaStream | null>(null);

    const [isStreaming, setIsStreaming] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [capturedBlob, setCapturedBlob] = useState<Blob | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

    const safeColors = {
        buttons: isValidHexColor(layout.color.buttons) ? layout.color.buttons : '#22FFAF',
        text: isValidHexColor(layout.color.text) ? layout.color.text : '#000000',
        hover: isValidHexColor(layout.color.hover) ? layout.color.hover : '#1DE9A3',
    };

    const startCamera = useCallback(async () => {
        try {
            setError(null);
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    facingMode,
                    width: { ideal: 1024 },
                    height: { ideal: 1024 },
                },
            });
            streamRef.current = stream;
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
                await videoRef.current.play();
            }
            setIsStreaming(true);
        } catch (err: any) {
            console.error('Camera access error:', err);
            setError('Could not access camera. Please allow camera permissions.');
        }
    }, [facingMode]);

    const stopCamera = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
        if (videoRef.current) {
            videoRef.current.srcObject = null;
        }
        setIsStreaming(false);
    }, []);

    const capturePhoto = useCallback(() => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;

        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        if (facingMode === 'user') {
            ctx.translate(canvas.width, 0);
            ctx.scale(-1, 1);
        }
        ctx.drawImage(video, 0, 0);

        canvas.toBlob(
            (blob) => {
                if (blob) {
                    setCapturedBlob(blob);
                    setPreviewUrl(URL.createObjectURL(blob));
                    stopCamera();
                }
            },
            'image/jpeg',
            0.92
        );
    }, [facingMode, stopCamera]);

    const retake = useCallback(() => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setCapturedBlob(null);
        setPreviewUrl(null);
        startCamera();
    }, [previewUrl, startCamera]);

    const confirmPhoto = useCallback(() => {
        if (capturedBlob) {
            setCapturedImage(capturedBlob);
            setScreen('form');
        }
    }, [capturedBlob, setCapturedImage, setScreen]);

    useEffect(() => {
        startCamera();
        return () => {
            stopCamera();
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!previewUrl) {
            stopCamera();
            startCamera();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [facingMode]);

    return (
        <div style={{ fontFamily: "'Nunito Sans', sans-serif" }} className="w-full max-w-[440px] p-8 rounded-xl bg-black shadow-[0_4px_20px_rgba(0,0,0,0.5)] flex flex-col items-center">
            <h2 className="text-[22px] font-bold mb-2" style={{ color: safeColors.buttons }}>Take a Selfie</h2>
            <div className="text-gray-400 mb-5 text-center text-sm">
                Front-facing, good lighting
            </div>

            {error && <div className="text-red-500 text-sm mb-4 text-center">{error}</div>}

            {/* Circular camera preview */}
            <div className="relative mb-4" style={{
                width: 220, height: 220, borderRadius: '50%', overflow: 'hidden',
                backgroundColor: '#151515',
                border: `3px solid ${previewUrl ? safeColors.buttons : '#333'}`,
                transition: 'border-color 0.3s',
            }}>
                {!previewUrl ? (
                    <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        muted
                        className="w-full h-full object-cover"
                        style={facingMode === 'user' ? { transform: 'scaleX(-1)' } : {}}
                    />
                ) : (
                    <img
                        src={previewUrl}
                        alt="Captured"
                        className="w-full h-full object-cover"
                    />
                )}

                {!previewUrl && isStreaming && (
                    <button
                        className="absolute top-2 right-2 bg-black/60 rounded-full p-2 text-white hover:bg-black/80"
                        onClick={() => setFacingMode((m) => (m === 'user' ? 'environment' : 'user'))}
                        title="Switch camera"
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 19H4a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h5" />
                            <path d="M13 5h7a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-5" />
                            <circle cx="12" cy="12" r="3" />
                            <path d="m18 22-3-3 3-3" />
                            <path d="m6 2 3 3-3 3" />
                        </svg>
                    </button>
                )}
            </div>

            <canvas ref={canvasRef} className="hidden" />

            <div className="flex gap-3 mt-2">
                <button
                    className="bg-[#222] hover:bg-[#3a3a3a] rounded-lg px-5 py-[12px] font-bold text-white text-sm"
                    onClick={() => {
                        stopCamera();
                        setCapturedImage(null);
                        setScreen('intro');
                    }}
                >
                    Start Over
                </button>

                {!previewUrl ? (
                    <button
                        className={`rounded-lg px-6 py-[12px] font-bold text-sm ${!isStreaming ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
                        style={{ backgroundColor: safeColors.buttons, color: safeColors.text }}
                        onClick={capturePhoto}
                        disabled={!isStreaming}
                    >
                        Capture
                    </button>
                ) : (
                    <>
                        <button
                            className="bg-[#222] hover:bg-[#3a3a3a] rounded-lg px-5 py-[12px] font-bold text-white text-sm"
                            onClick={retake}
                        >
                            Retake
                        </button>
                        <button
                            className="rounded-lg px-6 py-[12px] font-bold text-sm cursor-pointer"
                            style={{ backgroundColor: safeColors.buttons, color: safeColors.text }}
                            onClick={confirmPhoto}
                        >
                            Continue →
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

export default WebcamCapture;
